import { useTranslation } from 'react-i18next';
import invoices from '@/enterprise/assets/invoicesgrey.svg?url';
import { useBuyerNavigationLayoutContext } from '@/enterprise/components/BuyerNavigationLayout';
import NoDataState from '@/enterprise/components/NoDataState';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TablePagination,
  TableRow,
  TableSortArrow,
  TableDisplay,
  TableCell,
  TableDisplaySkeletonRows,
  TableSortDirection,
} from '@/enterprise/components/Table';
import useAwardedInvoices, {
  AwardedInvoiceFetchOptions,
  AwardedInvoiceSort,
} from '@/enterprise/data/useAwardedInvoices';
import useAwardedInvoiceStats from '@/enterprise/data/useAwardedInvoiceStats';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import getInvoiceDisplay from '@/utils/getInvoiceDisplay';
import useLocaleFormat from '@/utils/useLocaleFormat';
import useUserHasIndiaDivisions from '@/utils/useUserHasIndiaDivisions';

type AwardedInvoiceSortKey = Exclude<AwardedInvoiceSort, `-${string}`>;

interface AwardedInvoiceTableSort {
  key: AwardedInvoiceSortKey;
  direction: TableSortDirection;
}

const AwardedInvoiceTable = ({
  handleSort,
  options,
  pagination,
  takerMarket,
  sort,
}: {
  handleSort: (key: AwardedInvoiceSortKey) => void;
  options: Partial<AwardedInvoiceFetchOptions>;
  pagination: {
    currentPage: number;
    onNextChange: () => void;
    onPageSizeChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    onPrevChange: () => void;
    pageSize: number;
  };
  takerMarket: TakerMarket;
  sort: AwardedInvoiceTableSort;
}) => {
  const { t } = useTranslation();
  const hasIndiaDivisions = useUserHasIndiaDivisions();
  const { asCurrency, asDateStringText, asPercent } = useLocaleFormat();
  const {
    urlParams: { marketId, takerId },
  } = useBuyerNavigationLayoutContext();
  const {
    data: awardedInvoices,
    isFetching,
    isPending,
    isSuccess,
  } = useAwardedInvoices({
    marketId,
    takerId,
    options: {
      ...options,
      includeOnlyPendingManualAward: takerMarket.offerConfig.isManuallyAwarded,
    },
  });

  // We've already got the first set, but now we are getting an additional page.
  const isFetchingAdditionalInvoices = isSuccess && isFetching;

  const { data: awardedInvoiceStats } = useAwardedInvoiceStats({
    marketId,
    takerId,
    options: {
      includeOnlyPendingManualAward: takerMarket.offerConfig.isManuallyAwarded,
    },
  });

  return (
    <>
      <div className="border-b border-stroke bg-canvas px-4 py-6">
        <div className="flex flex-wrap items-center gap-1">
          <div className="font-medium">{t('core.awarded')}:</div>
          <div>{asCurrency(awardedInvoiceStats?.subTotal ?? 0, takerMarket.currency)}</div>
        </div>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead isSorted={sort.key === 'isCleared'} onClick={() => handleSort('isCleared')}>
              <TableSortArrow accessorKey="isCleared" sort={sort}>
                {t('core.status')}
              </TableSortArrow>
            </TableHead>
            <TableHead
              className="max-w-[224px]"
              isSorted={sort.key === 'voucherId'}
              onClick={() => handleSort('voucherId')}
            >
              <TableSortArrow accessorKey="voucherId" sort={sort}>
                {t('core.invoiceId')}
              </TableSortArrow>
            </TableHead>
            <TableHead isSorted={sort.key === 'dueDate'} onClick={() => handleSort('dueDate')}>
              <TableSortArrow accessorKey="dueDate" sort={sort} textRight>
                {hasIndiaDivisions ? t('core.tentativePaymentDate') : t('core.originalDueDate')}
              </TableSortArrow>
            </TableHead>
            <TableHead isSorted={sort.key === 'normalizedClearDate'} onClick={() => handleSort('normalizedClearDate')}>
              <TableSortArrow accessorKey="normalizedClearDate" sort={sort} textRight>
                {t('core.marketClearDate')}
              </TableSortArrow>
            </TableHead>
            <TableHead isSorted={sort.key === 'newPayDate'} onClick={() => handleSort('newPayDate')}>
              <TableSortArrow accessorKey="newPayDate" sort={sort} textRight>
                {t('core.newPayDate')}
              </TableSortArrow>
            </TableHead>
            <TableHead isSorted={sort.key === 'amount'} onClick={() => handleSort('amount')}>
              <TableSortArrow accessorKey="amount" sort={sort} textRight>
                {t('core.amount')}
              </TableSortArrow>
            </TableHead>
            <TableHead isSorted={sort.key === 'minDiscount'} onClick={() => handleSort('minDiscount')}>
              <TableSortArrow accessorKey="minDiscount" sort={sort} textRight>
                {t('core.discountWithPercent')}
              </TableSortArrow>
            </TableHead>
            <TableHead isSorted={sort.key === 'earn'} onClick={() => handleSort('earn')}>
              <TableSortArrow accessorKey="earn" sort={sort} textRight>
                {t('core.discount')}
              </TableSortArrow>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody loading={isFetchingAdditionalInvoices}>
          {isSuccess && awardedInvoices.length > 0 ? (
            awardedInvoices.map((awardedInvoice) => {
              const nextClearTime = awardedInvoice.clearDate ?? takerMarket.marketNextClearTime;
              return (
                <TableRow key={awardedInvoice.id} className="border-b">
                  <TableCell>
                    <span className={getInvoiceDisplay(awardedInvoice).color}>
                      {t(getInvoiceDisplay(awardedInvoice).translation)}
                    </span>
                  </TableCell>
                  <TableCell className="max-w-[224px] whitespace-pre-wrap break-words">
                    {awardedInvoice.voucherId}
                  </TableCell>
                  <TableCell className="text-right">
                    {awardedInvoice.dueDate && asDateStringText({ date: awardedInvoice.dueDate })}
                  </TableCell>
                  <TableCell className="text-right">
                    {nextClearTime && asDateStringText({ date: nextClearTime })}
                  </TableCell>
                  <TableCell className="text-right">
                    {awardedInvoice.newPayDate && asDateStringText({ date: awardedInvoice.newPayDate })}
                  </TableCell>
                  <TableCell className="text-right">
                    {asCurrency(awardedInvoice.amount, awardedInvoice.currency)}
                  </TableCell>
                  <TableCell className="text-right">
                    {asPercent({
                      value: awardedInvoice.minDiscount / 100,
                      decimals: 2,
                    })}
                  </TableCell>
                  <TableCell className="text-right">
                    {asCurrency(awardedInvoice.earn, awardedInvoice.currency)}
                  </TableCell>
                </TableRow>
              );
            })
          ) : isPending ? (
            <TableDisplaySkeletonRows colSpan={8} rowCount={options.page?.limit} />
          ) : (
            <TableDisplay colSpan={8}>
              <NoDataState
                className="py-8"
                icon={<img src={invoices} alt="invoices" className="h-28 w-28" />}
                title={t('invoices.noAvailableInvoices')}
                message={t('invoices.noAvailableAwardedInvoicesDescription')}
              />
            </TableDisplay>
          )}
        </TableBody>
      </Table>
      <TablePagination
        className="mt-auto"
        disableNext={
          pagination.currentPage ===
          Math.ceil((awardedInvoiceStats?.count ?? pagination.pageSize) / pagination.pageSize)
        }
        disablePrev={pagination.currentPage === 1}
        onNextChange={() => pagination.onNextChange()}
        onPageSizeChange={(e) => pagination.onPageSizeChange(e)}
        onPrevChange={() => pagination.onPrevChange()}
        pageSize={pagination.pageSize}
      />
    </>
  );
};

export default AwardedInvoiceTable;
